import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { USE_MOCK, surveyApi } from '@/api/client';
import { MOCK_TIPS } from '@/data/mockCatalog';
import type { TipItem } from '@/api/types';

export function useTips(fundamentalId?: string) {
  return useQuery<TipItem[]>({
    queryKey: ['tips', fundamentalId ?? 'all'],
    queryFn: async () => {
      if (USE_MOCK) {
        const tips = MOCK_TIPS as unknown as TipItem[];
        return fundamentalId ? tips.filter(t => t.fundamentalId === fundamentalId) : tips;
      }
      return surveyApi.getTips(fundamentalId);
    },
  });
}

export function useCreateTip() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Omit<TipItem, 'id'>) => {
      if (USE_MOCK) {
        if (import.meta.env.DEV) console.log('[mock] createTip', body);
        return Promise.resolve({ ...body, id: crypto.randomUUID() } as TipItem);
      }
      return surveyApi.createTip(body);
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['tips'] }); },
  });
}

export function useUpdateTip() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: TipItem) => {
      if (USE_MOCK) {
        if (import.meta.env.DEV) console.log('[mock] updateTip', id, body);
        return Promise.resolve({ id, ...body } as TipItem);
      }
      return surveyApi.updateTip(id, body);
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['tips'] }); },
  });
}

export function useDeleteTip() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => {
      if (USE_MOCK) {
        if (import.meta.env.DEV) console.log('[mock] deleteTip', id);
        return Promise.resolve();
      }
      return surveyApi.deleteTip(id);
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['tips'] }); },
  });
}
